import { FC } from 'react';

import { QuestionScheduleRowSkeleton } from './QuestionScheduleRowSkeleton';

import { RefreshIcon } from '@/assets/icons';
import { IconButton, Tag } from '@/components/atoms';
import { ScheduledQuestion } from '@/types/management/managementSchedule';
import { formatRowDate } from '@/utils/formatRowDate';
import { cn } from '@/utils/tailwind';

import { TableCell, TableRow } from '../Table/Table';

export type QuestionScheduleRowProps = {
  rowData: ScheduledQuestion;
  onClick: (rowData: ScheduledQuestion) => void;
  isLoading?: boolean;
};

export const QuestionScheduleRow: FC<QuestionScheduleRowProps> = ({
  rowData,
  onClick,
  isLoading,
}) => {
  const { date, question, category, status, lastAsked } = rowData;
  if (isLoading) {
    return <QuestionScheduleRowSkeleton />;
  }

  const { day: dayDate, month: monthDate } = formatRowDate(date);
  const { full: fullDateLastAsked } = formatRowDate(lastAsked);

  const showRefreshButton = status === 'UPCOMING';
  const showActiveBadge = status === 'ACTIVE';

  const getStatusTag = () => {
    switch (status) {
      case 'UPCOMING': {
        return (
          <Tag
            label="Upcoming"
            size="regular"
            color="blue"
            className="inline-flex max-w-full overflow-hidden"
            labelClassName="capitalize truncate"
          />
        );
      }
      case 'ACTIVE': {
        return (
          <Tag
            label="Active"
            size="regular"
            color="cyan"
            className="inline-flex max-w-full overflow-hidden"
            labelClassName="capitalize truncate"
          />
        );
      }
      case 'FINISHED': {
        return (
          <Tag
            label="Finished"
            size="regular"
            color="grey"
            className="inline-flex max-w-full overflow-hidden"
            labelClassName="capitalize truncate"
          />
        );
      }
    }
  };

  return (
    <TableRow className={cn({ 'opacity-40 pointer-events-none': status === 'FINISHED' })}>
      <TableCell className="relative flex-col gap-1 items-start">
        <p>{monthDate}</p>
        <p className="text-body-12-regular text-icons-secondary">{dayDate}</p>
        {showActiveBadge && (
          <span className="absolute top-1 bottom-1 left-0 w-2 rounded-tr-[4px] rounded-br-[4px] bg-custom-cyan-500" />
        )}
      </TableCell>
      <TableCell className="text-body-14-semibold">{question}</TableCell>
      <TableCell>
        <Tag
          label={category}
          size="regular"
          color="cyan"
          className="inline-flex max-w-full overflow-hidden"
          labelClassName="truncate"
        />
      </TableCell>
      <TableCell>{getStatusTag()}</TableCell>
      <TableCell>
        <p className="text-custom-warm-grey-700">{fullDateLastAsked}</p>
      </TableCell>
      <TableCell className="w-[72px]">
        {showRefreshButton && (
          <IconButton
            data-testid="refresh-button"
            onClick={() => onClick(rowData)}
            size="sm"
            variant="teritary-bright"
            icon={<RefreshIcon width={20} height={20} />}
          />
        )}
      </TableCell>
    </TableRow>
  );
};
